import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Gauge,
  TrendingUp,
  Info
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const SpendingLimits = ({ user }) => {
  const navigate = useNavigate();

  // Fetch spending limits
  const { data: limits, isLoading } = useQuery({
    queryKey: ["spendingLimits"],
    queryFn: async () => {
      const response = await axios.get(`${API}/card/spending-limits`);
      return response.data;
    }
  });

  const formatCurrency = (amount) => {
    return `J$${parseFloat(amount).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "Excellent": return "text-emerald-400";
      case "Good": return "text-green-400";
      case "Fair": return "text-yellow-400";
      default: return "text-orange-400";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="spending-limits-page">
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/card")}
          className="text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Spending Limits</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Health Score Tier */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Financial Health</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">{limits?.health_score || 0}</span>
                <span className={`text-sm font-medium ${getTierColor(limits?.health_tier)}`}>
                  {limits?.health_tier}
                </span>
              </div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 via-yellow-500 to-emerald-500 transition-all duration-500"
              style={{ width: `${limits?.health_score || 0}%` }}
            />
          </div>
        </div>

        {/* Limits Display */}
        <div className="glass rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Daily Spend Limit</p>
                <p className="text-xl font-mono font-semibold">{formatCurrency(limits?.daily_limit || 0)}</p>
              </div>
            </div>
            
            {/* Visual progress - show a sample usage */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Used today</span>
                <span>J$0 of {formatCurrency(limits?.daily_limit || 0)}</span>
              </div>
              <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 w-0" />
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Gauge className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Spend Limit</p>
                <p className="text-xl font-mono font-semibold">{formatCurrency(limits?.monthly_limit || 0)}</p>
              </div>
            </div>
            
            {/* Visual progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Used this month</span>
                <span>J$0 of {formatCurrency(limits?.monthly_limit || 0)}</span>
              </div>
              <div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 w-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="glass rounded-2xl p-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {limits?.limits_note || "Limits adjust automatically based on your Financial Health Score."}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-2">
                Improve your score by saving regularly, paying bills on time, and diversifying your wallet.
              </p>
            </div>
          </div>
        </div>

        {/* Tier Breakdown */}
        <div className="glass rounded-2xl p-5">
          <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-4">
            Limit Tiers
          </p>
          <div className="space-y-3">
            {[
              { tier: "Excellent", score: "85+", daily: "J$100,000", monthly: "J$500,000", color: "text-emerald-400" },
              { tier: "Good", score: "70-84", daily: "J$75,000", monthly: "J$350,000", color: "text-green-400" },
              { tier: "Fair", score: "50-69", daily: "J$50,000", monthly: "J$200,000", color: "text-yellow-400" },
              { tier: "Needs Work", score: "<50", daily: "J$25,000", monthly: "J$100,000", color: "text-orange-400" },
            ].map((t) => (
              <div 
                key={t.tier} 
                className={`flex items-center justify-between p-3 rounded-xl ${
                  limits?.health_tier === t.tier ? "bg-primary/10 border border-primary/30" : "bg-white/5"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${t.color}`}>{t.tier}</span>
                  <span className="text-xs text-muted-foreground">({t.score})</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{t.daily}/day</p>
                  <p className="text-xs text-muted-foreground">{t.monthly}/mo</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default SpendingLimits;
